import type { StructuredContentRoot } from "@repo/app-types/content/structured/types";
import type { AutomationArtifactDetail } from "@repo/app-types/entities/automationArtifact";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { MarkdownContent } from "../markdown/markdownContent.js";
import { renderStructuredContent } from "../structuredContent/structuredContentRenderer.js";

export interface AutomationStructuredContentProps {
  content: StructuredContentRoot;
}

export const AutomationStructuredContent: FC<
  AutomationStructuredContentProps
> = ({ content }) => (
  <div className="text-sm leading-relaxed text-foreground">
    {renderStructuredContent(content, {})}
  </div>
);

export interface AutomationArtifactContentProps {
  artifact: AutomationArtifactDetail;
}

export interface AutomationArtifactMarkdownContentProps {
  markdown: string;
}

/** Renders an artifact's saved source text through the Markdown renderer. */
export const AutomationArtifactMarkdownContent: FC<
  AutomationArtifactMarkdownContentProps
> = (props) => {
  const { markdown } = props;

  return (
    <div
      className={twMerge(
        "prose prose-sm max-w-none text-sm leading-relaxed prose-gray",
        "prose-headings:mt-3 prose-headings:mb-2 prose-headings:text-sm prose-headings:font-medium",
        "prose-p:mb-2 prose-p:text-sm prose-p:leading-relaxed",
        "prose-ul:mb-2 prose-ul:text-sm prose-ul:leading-relaxed",
        "prose-ol:mb-2 prose-ol:text-sm prose-ol:leading-relaxed",
        "prose-li:text-sm prose-li:leading-relaxed",
      )}
    >
      <MarkdownContent>{markdown}</MarkdownContent>
    </div>
  );
};

/**
 * Renders a saved automation result's body. Legacy `llm_output` artifacts fall back
 * to plain Markdown of the raw automation output.
 */
export const AutomationArtifactContent: FC<AutomationArtifactContentProps> = ({
  artifact,
}) => {
  if (artifact.content.type === "structured") {
    return <AutomationStructuredContent content={artifact.content.content} />;
  }

  return <AutomationArtifactMarkdownContent markdown={artifact.content.text} />;
};
